import React, { useEffect, useRef, useState } from 'react';
import { Loader2, AlertCircle, CheckCircle2 } from 'lucide-react';

interface PayTRIframeProps {
  token: string;
  onSuccess?: () => void;
  onError?: (message: string) => void;
  onClose?: () => void;
}

/**
 * PayTR ödeme iframe'ini render eden bileşen.
 * PayTR, ödeme sonucunu parent window'a postMessage ile bildirir.
 */
const PayTRIframe: React.FC<PayTRIframeProps> = ({
  token,
  onSuccess,
  onError,
  onClose,
}) => {
  const iframeRef = useRef<HTMLIFrameElement>(null);
  const [isLoading, setIsLoading] = useState(true);
  const [status, setStatus] = useState<'loading' | 'ready' | 'success' | 'error'>('loading');
  const [errorMessage, setErrorMessage] = useState<string>('');

  // PayTR iframe URL
  const iframeSrc = `https://www.paytr.com/odeme/guvenli/${token}`;

  useEffect(() => {
    // PayTR'dan gelen mesajları dinle
    const handleMessage = (event: MessageEvent) => {
      // PayTR domain kontrolü
      if (!event.origin.includes('paytr.com')) {
        return;
      }

      const data = event.data;

      // PayTR başarılı ödeme mesajı
      if (data === 'success' || data?.status === 'success') {
        setStatus('success');
        onSuccess?.();
      }

      // PayTR başarısız ödeme mesajı
      if (data === 'failed' || data?.status === 'failed') {
        const message = data?.message || 'Ödeme işlemi başarısız oldu';
        setStatus('error');
        setErrorMessage(message);
        onError?.(message);
      }

      // PayTR iframe kapandı
      if (data === 'close' || data?.action === 'close') {
        onClose?.();
      }
    };

    window.addEventListener('message', handleMessage);

    return () => {
      window.removeEventListener('message', handleMessage);
    };
  }, [onSuccess, onError, onClose]);

  const handleIframeLoad = () => {
    setIsLoading(false);
    setStatus('ready');
  };

  const handleIframeError = () => {
    setIsLoading(false);
    setStatus('error');
    setErrorMessage('Ödeme sayfası yüklenemedi. Lütfen tekrar deneyin.');
    onError?.('Ödeme sayfası yüklenemedi');
  };

  if (status === 'success') {
    return (
      <div className="flex flex-col items-center justify-center p-8 bg-green-50 rounded-xl">
        <CheckCircle2 className="w-16 h-16 text-green-500 mb-4" />
        <h3 className="text-xl font-semibold text-green-800 mb-2">
          Ödeme Başarılı!
        </h3>
        <p className="text-green-600 text-center">
          Aboneliğiniz aktif edildi. Yönlendiriliyorsunuz...
        </p>
      </div>
    );
  }

  if (status === 'error' && errorMessage) {
    return (
      <div className="flex flex-col items-center justify-center p-8 bg-red-50 rounded-xl">
        <AlertCircle className="w-16 h-16 text-red-500 mb-4" />
        <h3 className="text-xl font-semibold text-red-800 mb-2">
          Ödeme Başarısız
        </h3>
        <p className="text-red-600 text-center mb-4">{errorMessage}</p>
        <button
          onClick={() => {
            setStatus('loading');
            setErrorMessage('');
            setIsLoading(true);
          }}
          className="px-6 py-2 bg-red-600 text-white rounded-lg hover:bg-red-700 transition-colors"
        >
          Tekrar Dene
        </button>
      </div>
    );
  }

  return (
    <div className="relative w-full" style={{ minHeight: '500px' }}>
      {/* Loading Overlay */}
      {isLoading && (
        <div className="absolute inset-0 flex flex-col items-center justify-center bg-slate-100 rounded-xl z-10">
          <Loader2 className="w-12 h-12 text-blue-600 animate-spin mb-4" />
          <p className="text-slate-600">Ödeme sayfası yükleniyor...</p>
        </div>
      )}

      {/* PayTR Iframe */}
      <iframe
        ref={iframeRef}
        src={iframeSrc}
        id="paytr_iframe"
        frameBorder="0"
        scrolling="yes"
        style={{
          width: '100%',
          height: '500px',
          border: 'none',
          borderRadius: '12px',
          display: isLoading ? 'none' : 'block',
        }}
        onLoad={handleIframeLoad}
        onError={handleIframeError}
        title="PayTR Ödeme"
        allow="payment"
      />

      {/* Güvenlik Bilgisi */}
      <div className="mt-4 flex items-center justify-center gap-2 text-sm text-slate-500">
        <svg
          className="w-4 h-4"
          fill="none"
          stroke="currentColor"
          viewBox="0 0 24 24"
        >
          <path
            strokeLinecap="round"
            strokeLinejoin="round"
            strokeWidth={2}
            d="M12 15v2m-6 4h12a2 2 0 002-2v-6a2 2 0 00-2-2H6a2 2 0 00-2 2v6a2 2 0 002 2zm10-10V7a4 4 0 00-8 0v4h8z"
          />
        </svg>
        <span>Güvenli ödeme - PayTR altyapısı ile korunmaktadır</span>
      </div>
    </div>
  );
};

export default PayTRIframe;

